import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

import Dialog from "components/dialog";
import EmailPreviewIframe from "components/email-preview-iframe";
import { useGetEmailQuery } from "services/emails";
import { formatDateOnlyValue } from "utils/common";

type SentLetterPreviewDialogProps = {
  /** The email that delivered the letter — `AmbientScribeNote.sent_email_id`. */
  emailId: number;
  onClose: () => void;
  /** Raise above a host dialog that lifted its own z-index (the scribe details modal). */
  zIndex?: number;
};

/**
 * What was actually emailed to the patient, for a clinician asking "did this go out, and what did
 * it say?".
 *
 * Reads the stored `Email`, not the generated note: `get_params()` writes the wrapped, delivered
 * HTML back onto that column at send time, so this reflects whatever the sender edited in the
 * compose modal. Reading the note instead would show the draft and quietly answer a different
 * question.
 *
 * Takes no `open` prop on purpose — the caller mounts it only while a preview is requested, which
 * paired with `keepUnusedDataFor: 0` on the query keeps pre-signed attachment URLs fresh. Same
 * arrangement as `PhysicianCommunicationDialog`, whose structure this follows.
 */
export default function SentLetterPreviewDialog({
  emailId,
  onClose,
  zIndex
}: SentLetterPreviewDialogProps) {
  const { data, isFetching, isError, refetch } = useGetEmailQuery({ emailId });

  const renderContent = () => {
    if (isFetching) {
      return (
        <Box
          sx={{ display: "flex", justifyContent: "center", py: 4 }}
          data-testid="avs-sent-preview-loading"
        >
          <CircularProgress size={28} />
        </Box>
      );
    }

    if (isError || !data) {
      // Not an empty body: telling someone the letter has no content when the request failed reads
      // as "nothing was sent", which is a different and wrong answer.
      return (
        <Stack spacing={2} alignItems="flex-start">
          <Typography data-testid="avs-sent-preview-error">
            We could not load this email.
          </Typography>
          <Button variant="contained" size="small" onClick={() => refetch()}>
            Try again
          </Button>
        </Stack>
      );
    }

    if (!data.html && !data.text) {
      // Reachable two ways: the email was soft-deleted (the endpoint returns a tombstone, not a
      // 404), or it is still queued with `send_at` in the future, in which case `html` holds the
      // raw fragment before the send-time wrap. Saying so is the honest answer.
      return (
        <Typography data-testid="avs-sent-preview-unavailable">
          This email is no longer available to preview.
        </Typography>
      );
    }

    const recipients = (data.participants ?? [])
      .map((participant) => participant.address)
      .filter(Boolean)
      .join(", ");

    return (
      <Stack spacing={2}>
        <Stack spacing={0.5}>
          <Typography variant="body2" color="text.secondary">
            Sent {formatDateOnlyValue(data.send_at || data.created_at)}
            {recipients ? ` to ${recipients}` : ""}
          </Typography>
          <Typography variant="subtitle2" data-testid="avs-sent-preview-subject">
            {data.subject}
          </Typography>
        </Stack>
        {/* EmailPreviewIframe, never WordProcessorHtmlContent: `Email.html` is not guaranteed
            sanitized, and the iframe is sandboxed without allow-scripts. */}
        <EmailPreviewIframe
          html={data.html}
          text={data.text}
          title="Email sent to patient"
          sx={{ height: "60vh", minHeight: 400 }}
        />
      </Stack>
    );
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title="After Visit Summary sent to patient"
      minWidth={720}
      maxWidth="md"
      sx={zIndex ? { zIndex } : undefined}
    >
      <Box data-testid="avs-sent-preview">{renderContent()}</Box>
    </Dialog>
  );
}
